/**
 * A dependency-free search helper — good enough for the editor's type-ahead.
 *
 * Deliberately small: case- and accent-insensitive prefix matching over a
 * per-entry token bag, AND semantics across the query tokens that carry
 * meaning, and a flat score so the obvious answer sorts first. No stemming, no
 * fuzzy matching, no index server. When the catalog outgrows this (or the
 * assistant needs semantic recall, PLAN §7.1) it gets replaced wholesale —
 * nothing else depends on the scoring.
 *
 * ```ts
 * searchCatalog('citra');                                   // the hop, first
 * searchCatalog('pilsner', { kinds: ['fermentable'] });      // malts only
 * searchCatalog('ipa', { kinds: ['style'], limit: 5 });
 * searchCatalog('US Cascade Hop Pellets 1 oz');              // what the bag says
 * ```
 */

import {
  type CatalogEntry,
  type CatalogEntryByKind,
  type CatalogKind,
  collections,
} from './catalog';
import { classAliases } from './data/fermentables/class-names';
import { makerReach } from './data/fermentables/makers';
import type { Fermentable } from './schema/fermentable';
import type { Hop } from './schema/hop';
import type { Misc } from './schema/misc';
import type { Style } from './schema/style';
import { styleCode } from './schema/style';
import type { Yeast } from './schema/yeast';

// ---------------------------------------------------------------------------
// Public shape
// ---------------------------------------------------------------------------

/** One search result, with the entry it points at. */
export interface SearchHit<K extends CatalogKind = CatalogKind> {
  /** Which collection the hit came from. */
  readonly kind: K;
  /** The entry's stable catalog id. */
  readonly id: string;
  /** The entry's display name. */
  readonly name: string;
  /** Relative relevance. Comparable within one result set, not across sets. */
  readonly score: number;
  /** The entry itself, fully typed. */
  readonly entry: CatalogEntryByKind[K];
}

/** Filters and limits for {@link searchCatalog}. */
export interface SearchOptions {
  /** Restrict to these kinds. Defaults to all five. */
  readonly kinds?: readonly CatalogKind[];
  /**
   * Restrict to these type tokens — see {@link typeTokenOf} for what each kind
   * contributes (`base`, `crystal-caramel`, `dual`, `kveik`, `acid`,
   * `category-21`, …). Matched case-insensitively; an entry passes if its type
   * token is in the list.
   */
  readonly types?: readonly string[];
  /** Maximum hits to return. Defaults to 20. */
  readonly limit?: number;
}

/**
 * The type token a kind filters on.
 *
 * Each kind has one obvious "what sort of thing is this" axis, and they are
 * flattened into a single string so the editor can pass one filter list
 * regardless of which kinds are in play. Styles have no type of their own, so
 * their BJCP category doubles as one.
 */
export function typeTokenOf(kind: CatalogKind, entry: CatalogEntry): string {
  switch (kind) {
    case 'fermentable':
      return (entry as Fermentable).type;
    case 'hop':
      return (entry as Hop).purpose;
    case 'yeast':
      return (entry as Yeast).type;
    case 'misc':
      return (entry as Misc).type;
    case 'style':
      return `category-${(entry as Style).categoryNumber}`;
  }
}

// ---------------------------------------------------------------------------
// Normalisation
// ---------------------------------------------------------------------------

/**
 * Lower-case, strip accents, and reduce anything that is not a letter or digit
 * to a space — so `Kölsch` matches `kolsch` and `W-34/70` matches `34 70`.
 */
export function normalize(value: string): string {
  return value
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

/** Split a normalised string into tokens. */
function tokenize(value: string): readonly string[] {
  const normalized = normalize(value);
  return normalized.length === 0 ? [] : normalized.split(' ');
}

// ---------------------------------------------------------------------------
// The query plan
// ---------------------------------------------------------------------------

/**
 * Words a brewer types because they are printed on the bag, not because they
 * narrow anything.
 *
 * A shop sells "US Cascade Hop Pellets 1 oz". Four of those six words describe
 * the packet rather than the hop, and under strict AND semantics every one of
 * them has to land somewhere or the query returns nothing — which is what it
 * did. These tokens are kept, and still score when they hit, but they no longer
 * have to hit. `Hop Hash` and `Northern Brewer (US)` are real catalog names, so
 * dropping the words outright would lose them; making them optional does not.
 *
 * Country abbreviations are here for the same reason. `US` used to work only by
 * accident, prefix-matching the `usa` tag that American entries happen to carry
 * and missing every German- or Czech-origin row that a US shop still labels
 * `US`, which is why `US Magnum` found nothing.
 */
const SOFT_TOKENS: ReadonlySet<string> = new Set([
  // Form and packaging. `cryo` is a form the recipe already models
  // (`DEFAULT_HOP_FORM_MULTIPLIERS`), so in a query it names the packet, not
  // a different plant: "Cryo Hops Cascade" is Cascade.
  'hop',
  'hops',
  'pellet',
  'pellets',
  'cryo',
  'leaf',
  'plug',
  'plugs',
  'cone',
  'cones',
  'whole',
  'bulk',
  'bag',
  'bags',
  'pack',
  'packet',
  // Country abbreviations as a shop prints them.
  'us',
  'usa',
  'uk',
  'gb',
  'gr',
  'de',
  'cz',
  'nz',
  'au',
  'aus',
]);

/**
 * Units, which are soft only in the pair they arrive in.
 *
 * A size is two tokens, `1 oz` or `500 g`, and it is the pair that is noise.
 * Neither half is safe to forgive on its own: `g` alone is a brewer one letter
 * into Galaxy, and forgiving it turns "Cara G" into every Cara malt we sell.
 */
const UNIT_TOKENS: ReadonlySet<string> = new Set([
  'oz',
  'lb',
  'lbs',
  'g',
  'kg',
  'ml',
  'gram',
  'grams',
]);

/**
 * Tokens that mean the same thing spelled two ways.
 *
 * Deliberately tiny, and only for abbreviations that are *general* — `Mt Hood`
 * for Mount Hood, `Doctor Rudi` for Dr. Rudi. An alias that belongs to one
 * entry (`CTZ`, `ADHA-483`, `HBC 369`) goes in that entry's `tags`, which is
 * what the catalog has always used for them and where a reader looking at the
 * hop will find it.
 *
 * Expansion is *additive*: a token keeps its own matches and gains its
 * siblings', scored as the better of the two. Replacing it would break the
 * type-ahead, where every prefix of a name is a live query and `mt` is
 * someone two letters into a hop we have not thought of.
 *
 * A class row's other *names* — `2-Row` for `2-Row Pale Malt`, `Carapils` for
 * `Carapils/Dextrin Malt` — are neither of these. They are indexed as names,
 * from the catalog's `CLASS_ALIASES`, so that the whole-name bonuses treat
 * them as the row's own.
 */
const TOKEN_EQUIVALENTS: readonly (readonly string[])[] = [
  ['mt', 'mount'],
  ['dr', 'doctor'],
  // Prefix matching runs one way only, so the shorter spelling reaches the
  // longer and not the other way about: `hallertau` finds Hallertauer
  // Mittelfrüh, `hallertauer` found nothing tagged `hallertau`.
  ['hallertau', 'hallertauer'],
];

const ALIASES: ReadonlyMap<string, readonly string[]> = new Map(
  TOKEN_EQUIVALENTS.flatMap((group) =>
    group.map((token): [string, readonly string[]] => [token, group.filter((it) => it !== token)]),
  ),
);

/**
 * The singular of a plain English plural, or `null`.
 *
 * Prefix matching already reaches a plural from its singular (`oat` finds
 * `oats`) but not the other way about, so `fuggles` found nothing: people say
 * the hop's name that way, and so does the assistant. Like the equivalents
 * above this is additive. `goldings` keeps its own match and only gains
 * `golding`'s. Five letters or more and no `ss`, so `pils`, `oats` and `bass`
 * stay as they are.
 */
function singularOf(token: string): string | null {
  return token.length > 4 && token.endsWith('s') && !token.endsWith('ss')
    ? token.slice(0, -1)
    : null;
}

function aliasesOf(token: string): readonly string[] {
  const aliases = ALIASES.get(token) ?? [];
  const singular = singularOf(token);
  return singular === null ? aliases : [...aliases, singular];
}

/** One query token, and whether an entry has to match it to be a hit at all. */
interface QueryToken {
  readonly text: string;
  /** Other spellings of {@link text} that count as the same match. */
  readonly aliases: readonly string[];
  /** `false` for packaging noise: it may score, but it cannot exclude. */
  readonly required: boolean;
}

/**
 * Split a normalised query into tokens and decide which of them are load-bearing.
 *
 * A size — `1 oz`, `11 lbs`, `500 g` — is dropped outright rather than
 * softened. Nothing in the catalog is ever the thing a brewer meant by "1 oz",
 * and leaving it in let `1` prefix-match its way onto HBC 1134.
 */
function planQuery(tokens: readonly string[]): readonly QueryToken[] {
  const isNumber = (value: string | undefined) => value !== undefined && /^\d+$/.test(value);
  const isSize = (text: string, index: number) =>
    (isNumber(text) && UNIT_TOKENS.has(tokens[index + 1] ?? '')) ||
    (UNIT_TOKENS.has(text) && isNumber(tokens[index - 1]));

  return tokens.flatMap((text, index) =>
    isSize(text, index)
      ? []
      : [
          {
            text,
            aliases: aliasesOf(text),
            required: !SOFT_TOKENS.has(text),
          },
        ],
  );
}

// ---------------------------------------------------------------------------
// The index
// ---------------------------------------------------------------------------

/**
 * What a query is matched against, in descending weight: the display name and
 * any other name the row goes by, then the entry's supporting identity
 * (maker/lab/origin/category/product code), then its tags.
 */
interface IndexedEntry {
  readonly kind: CatalogKind;
  readonly id: string;
  readonly name: string;
  readonly typeToken: string;
  /**
   * Every whole name this entry answers to, normalised: the display name
   * first, then the class aliases for a generic fermentable row.
   */
  readonly names: readonly string[];
  /** {@link names}, each with the word `malt` taken out. See {@link withoutMalt}. */
  readonly coreNames: readonly string[];
  /** The tokens of every name in {@link names}, deduplicated. */
  readonly nameTokens: readonly string[];
  readonly secondaryTokens: readonly string[];
  readonly tagTokens: readonly string[];
  /**
   * An unbranded class row — a `Generic` fermentable. When the words cannot
   * choose between it and a maltster's product it comes first, which is the
   * importer's rule and the type-ahead's now: a brewer who typed `Munich` and
   * nothing else did not ask for Malteurop's.
   */
  readonly classRow: boolean;
  readonly entry: CatalogEntry;
}

/** Score weights. Tuned by eye against the type-ahead, not by any theory. */
const WEIGHT = {
  nameExact: 60,
  namePrefix: 30,
  nameToken: 20,
  secondary: 10,
  tag: 6,
  /** Applied once when the entire query matches one of the entry's whole names. */
  fullNameExact: 80,
  /** Applied once when the entire query prefixes one of the entry's whole names. */
  fullNamePrefix: 25,
  /** Applied once when the entire query matches the id. */
  idExact: 90,
} as const;

function secondaryTextOf(kind: CatalogKind, entry: CatalogEntry): readonly string[] {
  switch (kind) {
    case 'fermentable': {
      const f = entry as Fermentable;
      return [f.maker, f.origin, f.type, f.maltClass];
    }
    case 'hop': {
      const h = entry as Hop;
      return [h.origin, h.growingRegion, h.cultivar, h.purpose, ...h.flavorTags].filter(
        (value): value is string => value !== null,
      );
    }
    case 'yeast': {
      const y = entry as Yeast;
      return [y.lab, y.productCode, y.type, y.form];
    }
    case 'misc': {
      const m = entry as Misc;
      return [m.type, m.use, m.form];
    }
    case 'style': {
      const s = entry as Style;
      return [s.categoryName, styleCode(s)];
    }
  }
}

/**
 * The word that names the trade rather than the product.
 *
 * `Munich` and `Munich Malt` are the same thing in a malt catalog, and the
 * whole-name bonuses would otherwise hand "munich" to whichever maltster
 * prints the short form and leave the class row, which prints the long one,
 * a mere prefix. Only the whole-name comparisons look through it: as a query
 * token `malt` still has to land somewhere, as it always did.
 */
function withoutMalt(normalized: string): string {
  return normalized
    .split(' ')
    .filter((token) => token !== 'malt')
    .join(' ');
}

function namesOf(kind: CatalogKind, entry: CatalogEntry): readonly string[] {
  const aliases = kind === 'fermentable' ? classAliases(entry.id) : [];
  return [entry.name, ...aliases];
}

function isClassRow(kind: CatalogKind, entry: CatalogEntry): boolean {
  return kind === 'fermentable' && makerReach((entry as Fermentable).maker) === 'generic';
}

function tagsOf(kind: CatalogKind, entry: CatalogEntry): readonly string[] {
  if (kind === 'hop') {
    const h = entry as Hop;
    return [...h.tags, ...h.flavorTags];
  }
  return (entry as Fermentable | Yeast | Misc | Style).tags;
}

function buildIndex(): readonly IndexedEntry[] {
  const indexed: IndexedEntry[] = [];
  for (const kind of Object.keys(collections) as CatalogKind[]) {
    for (const entry of collections[kind] as readonly CatalogEntry[]) {
      const names = namesOf(kind, entry).map(normalize);
      indexed.push({
        kind,
        id: entry.id,
        name: entry.name,
        typeToken: typeTokenOf(kind, entry),
        names,
        coreNames: names.map(withoutMalt),
        nameTokens: [...new Set(names.flatMap(tokenize))],
        secondaryTokens: secondaryTextOf(kind, entry).flatMap(tokenize),
        tagTokens: tagsOf(kind, entry).flatMap(tokenize),
        classRow: isClassRow(kind, entry),
        entry,
      });
    }
  }
  return indexed;
}

/** Built once at module load; the dataset is static. */
const INDEX = buildIndex();

// ---------------------------------------------------------------------------
// Scoring
// ---------------------------------------------------------------------------

/** The best score any spelling of one query token gets against one entry. */
function scoreQueryToken(candidate: IndexedEntry, token: QueryToken): number {
  let best = scoreToken(candidate, token.text);
  for (const alias of token.aliases) {
    best = Math.max(best, scoreToken(candidate, alias));
  }
  return best;
}

function scoreToken(candidate: IndexedEntry, token: string): number {
  let best = 0;
  for (const nameToken of candidate.nameTokens) {
    if (nameToken === token) best = Math.max(best, WEIGHT.nameExact);
    else if (nameToken.startsWith(token)) best = Math.max(best, WEIGHT.nameToken);
  }
  if (best === 0 && candidate.names.some((name) => name.startsWith(token))) {
    best = WEIGHT.namePrefix;
  }
  if (best === 0) {
    for (const secondary of candidate.secondaryTokens) {
      if (secondary.startsWith(token)) {
        best = WEIGHT.secondary;
        break;
      }
    }
  }
  if (best === 0) {
    for (const tag of candidate.tagTokens) {
      if (tag.startsWith(token)) {
        best = WEIGHT.tag;
        break;
      }
    }
  }
  return best;
}

function scoreEntry(
  candidate: IndexedEntry,
  tokens: readonly QueryToken[],
  normalizedQuery: string,
): number {
  /**
   * Whether the query is the beginning of this entry's name.
   *
   * This is what decides whether a soft token is allowed to *rank* as well as
   * match, and it is the whole difficulty of the pass in one line. The same
   * word is signal in one query and noise in the next:
   *
   * - "Citra Hop Pellets" — `hop` is a name token of Citra CO₂ Hop Extract, so
   *   rewarding it put the extract above Citra itself. Noise.
   * - "Hop Ha…" — a brewer three letters into Hop Hash. Zeroing `hop` there
   *   left `ha` to rank alone and handed the type-ahead to a yeast. Signal.
   *
   * A phrase prefix of the name separates them: `hop hash` starts with `hop
   * ha`, `citra co2 hop extract` does not start with `citra hop pellets`. So a
   * soft token scores where the query is genuinely naming the entry, and
   * scores zero where it merely brushes past it.
   */
  const namesThisEntry = candidate.names.some((name) => name.startsWith(normalizedQuery));

  /**
   * Whether the query says anything beyond the packaging at all. When it does
   * not, there is nothing else to rank on, so soft tokens score normally and
   * "hop" still finds Hop Hash.
   */
  const ranksOnMeaning = tokens.some((token) => token.required);

  let total = 0;
  let landed = 0;
  for (const token of tokens) {
    const tokenScore = scoreQueryToken(candidate, token);
    // AND semantics, over the tokens that carry meaning: every required token
    // has to land somewhere. Packaging noise is forgiven when it does not.
    if (tokenScore === 0) {
      if (token.required) return 0;
      continue;
    }
    landed += 1;
    if (token.required || !ranksOnMeaning || namesThisEntry) total += tokenScore;
  }
  // A query of nothing but noise ("hop pellets") still has to hit something to
  // be a hit; otherwise it would match the whole catalog.
  if (landed === 0) return 0;
  // The whole-name bonuses read every name the entry goes by, and read
  // `Munich` and `Munich Malt` as the same name. A query that is nothing but
  // `malt` has no core to compare, and gets the raw comparison alone.
  const core = withoutMalt(normalizedQuery);
  const wholeName =
    candidate.names.includes(normalizedQuery) ||
    (core !== '' && candidate.coreNames.includes(core));
  const startOfName =
    candidate.names.some((name) => name.startsWith(normalizedQuery)) ||
    (core !== '' && candidate.coreNames.some((name) => name.startsWith(core)));
  if (wholeName) total += WEIGHT.fullNameExact;
  else if (startOfName) total += WEIGHT.fullNamePrefix;
  if (normalize(candidate.id) === normalizedQuery) total += WEIGHT.idExact;
  return total;
}

// ---------------------------------------------------------------------------
// Entry point
// ---------------------------------------------------------------------------

/** The default number of hits returned. */
export const DEFAULT_SEARCH_LIMIT = 20;

/**
 * Search the catalog.
 *
 * An empty (or whitespace-only) query returns the first `limit` entries of the
 * selected kinds, in catalog order — which is what a type-ahead wants to show
 * before the user has typed anything.
 *
 * @param query - Free text, up to and including a shop's whole product name.
 *   Tokens are matched as prefixes and ANDed, except for the packaging and
 *   country words in {@link SOFT_TOKENS}, which score when they land and are
 *   forgiven when they do not.
 * @param options - Kind/type filters and a result limit.
 * @returns Hits sorted by descending score, ties broken by the class row
 *   first, then by name, then by id.
 */
export function searchCatalog(query: string, options: SearchOptions = {}): readonly SearchHit[] {
  const limit = options.limit ?? DEFAULT_SEARCH_LIMIT;
  const kinds = options.kinds;
  const types = options.types?.map((type) => type.toLowerCase());

  const eligible = INDEX.filter(
    (candidate) =>
      (kinds === undefined || kinds.includes(candidate.kind)) &&
      (types === undefined || types.includes(candidate.typeToken.toLowerCase())),
  );

  const normalizedQuery = normalize(query);
  if (normalizedQuery.length === 0) {
    return eligible.slice(0, Math.max(0, limit)).map((candidate) => toHit(candidate, 0));
  }

  const tokens = planQuery(normalizedQuery.split(' '));
  const scored: { candidate: IndexedEntry; score: number }[] = [];
  for (const candidate of eligible) {
    const score = scoreEntry(candidate, tokens, normalizedQuery);
    if (score > 0) scored.push({ candidate, score });
  }

  scored.sort(
    (a, b) =>
      b.score - a.score ||
      Number(b.candidate.classRow) - Number(a.candidate.classRow) ||
      a.candidate.name.localeCompare(b.candidate.name) ||
      a.candidate.id.localeCompare(b.candidate.id),
  );

  return scored.slice(0, Math.max(0, limit)).map(({ candidate, score }) => toHit(candidate, score));
}

function toHit(candidate: IndexedEntry, score: number): SearchHit {
  return {
    kind: candidate.kind,
    id: candidate.id,
    name: candidate.name,
    score,
    // The index erases the kind→entry correlation; the builder guarantees it.
    entry: candidate.entry as CatalogEntryByKind[CatalogKind],
  };
}
